import React from 'react';
import type ServiceDeskItsmSampleSpaceType from '@jira/spa-apps__servicedesk-itsm-sample-space/src/index.tsx';
import { LazyAtlassianNavigation } from '@jira/navigation-apps__atlassian-navigation/src/async.tsx';
import { forgeProjectModuleResource } from '@jira/navigation-apps__resources/src/controllers/forge/index.tsx';
import { staticProjectSidebarConnectAddonsNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar-connect-addons/index.tsx';
import { staticProjectSidebarNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar/index.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { getNavigationResources } from '@jira/router-resources__navigation/src/getNavigationResources.tsx';
import { projectContextResource } from '@jira/router-resources__project-context/src/projectContextResource.tsx';
import { incidentExperimentTraitResource } from '@jira/router-resources__software-incidents/src/incidentExperimentTraitResource.tsx';
import { servicedeskItsmSampleSpaceRoute } from '@jira/router-routes__servicedesk-itsm-sample-space-routes/src/servicedeskItsmSampleSpaceRoute.tsx';
import { serviceProjectLayout } from '@jira/platform__layouts-service-project-layout/src/index.tsx';
// eslint-disable-next-line jira/restricted/@jira+servicedesk__common-toPackageName
import { APP_NAMES, toPackageName } from '@jira/servicedesk__common/src/toPackageName.tsx';
import { LazyPage } from '@jira/platform__spa/src/view/lazy-page/index.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import type { Route } from '@atlassian/react-resource-router';

const LazyItsmSampleSpace = lazyForPaint<typeof ServiceDeskItsmSampleSpaceType>(
	() =>
		import(
			/* webpackChunkName: "async-servicedesk-itsm-sample-space", jiraSpaEntry: "async-servicedesk-itsm-sample-space" */ '@jira/spa-apps__servicedesk-itsm-sample-space/src/index.tsx'
		),
);

const ItsmSampleSpace = () => (
	<LazyPage
		Page={LazyItsmSampleSpace}
		pageId="spa-apps/servicedesk/itsm-sample-space"
		shouldShowSpinner
	/>
);

export const servicedeskItsmSampleSpaceRouteEntry: Route = createEntry(
	servicedeskItsmSampleSpaceRoute,
	() => ({
		group: 'servicedesk',
		isRedirect: true,
		// eslint-disable-next-line no-restricted-syntax
		component: ItsmSampleSpace,
		layout: serviceProjectLayout,

		resources: [
			...getNavigationResources(),
			// these have been expanded from getNavigationSidebarProjectResource - please examine whether your route requires these resources in nav4 and remove them if needed
			// A guide to check & cleanup: https://hello.atlassian.net/wiki/x/Q8XybwE
			incidentExperimentTraitResource,
			// eslint-disable-next-line jira/router/no-project-context-resource
			projectContextResource,
			forgeProjectModuleResource,
			staticProjectSidebarNavigationResource,
			staticProjectSidebarConnectAddonsNavigationResource,
			// end of resources expanded from getNavigationSidebarProjectResource
		],

		forPaint: [LazyAtlassianNavigation, LazyItsmSampleSpace],
		perfMetricKey: 'jsm-sample-space-landing-page',

		meta: {
			reporting: {
				id: APP_NAMES.ITSM_SAMPLE_SPACE,
				packageName: toPackageName(APP_NAMES.ITSM_SAMPLE_SPACE),
				teamName: 'Sequoia',
			},
		},

		bifrost: {
			bifrostGroup: 'shell-only',
		},
	}),
);
